import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Button, Space, Tag, Tabs, Tooltip, message } from 'antd';
import { BarChartOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../utils/cicloEscolar';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Option } = Select;

// Convierte valor (string, number o null) a número o null
const toNum = (v) => (v === null || v === undefined || v === '') ? null : Number(v);

// Celda de nota con color
const CeldaNota = ({ valor }) => {
  const n = toNum(valor);
  if (n === null) return <Tag color="default">-</Tag>;
  const color = n >= 60 ? 'blue' : 'red';
  return <Tag color={color}>{n.toFixed(2)}</Tag>;
};

const CICLO_ACTUAL = getCicloActual();

const PromediosGrado = () => {
  const [tab, setTab] = useState('grado');

  // Catálogos
  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  // Filtros tab 1 — Por Grado
  const [filtrosGrado, setFiltrosGrado] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdJornada: null,
    IdGrado: null,
    IdSeccion: null,
  });

  // Filtros tab 2 — Por Jornada
  const [filtrosJornada, setFiltrosJornada] = useState({
    p_CicloEscolar: CICLO_ACTUAL,
    IdJornada: null,
    IdGrado: null,
  });

  const [dataGrado, setDataGrado] = useState([]);
  const [dataJornada, setDataJornada] = useState([]);
  const [loadingGrado, setLoadingGrado] = useState(false);
  const [loadingJornada, setLoadingJornada] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  useEffect(() => {
    cargarCatalogos();
  }, []);

  const cargarCatalogos = async () => {
    try {
      const [gradosRes, seccionesRes, jornadasRes] = await Promise.all([
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas'),
      ]);
      setGrados((gradosRes.data.data || gradosRes.data || []).filter(g => g.IdGrado != null));
      setSecciones((seccionesRes.data.data || seccionesRes.data || []).filter(s => s.IdSeccion != null));
      setJornadas((jornadasRes.data.data || jornadasRes.data || []).filter(j => j.IdJornada != null));
    } catch {
      message.error('Error al cargar catálogos');
    } finally {
      setLoadingCatalogos(false);
    }
  };

  const buscarPorGrado = async () => {
    setLoadingGrado(true);
    try {
      const params = { p_CicloEscolar: filtrosGrado.p_CicloEscolar };
      if (filtrosGrado.IdJornada) params.IdJornada = filtrosGrado.IdJornada;
      if (filtrosGrado.IdGrado)   params.IdGrado   = filtrosGrado.IdGrado;
      if (filtrosGrado.IdSeccion) params.IdSeccion  = filtrosGrado.IdSeccion;

      const res = await apiClient.get('/reportes/promedios-por-grado', { params });
      setDataGrado(res.data.data || []);
    } catch {
      message.error('Error al cargar promedios por grado');
    } finally {
      setLoadingGrado(false);
    }
  };

  const buscarPorJornada = async () => {
    setLoadingJornada(true);
    try {
      const params = { p_CicloEscolar: filtrosJornada.p_CicloEscolar };
      if (filtrosJornada.IdJornada) params.IdJornada = filtrosJornada.IdJornada;
      if (filtrosJornada.IdGrado)   params.IdGrado   = filtrosJornada.IdGrado;

      const res = await apiClient.get('/reportes/promedios-por-jornada', { params });
      setDataJornada(res.data.data || []);
    } catch {
      message.error('Error al cargar promedios por jornada');
    } finally {
      setLoadingJornada(false);
    }
  };

  // ── Columnas comunes ──────────────────────────────────────────────────────
  const sortNum = (campo) => (a, b) => (toNum(a[campo]) ?? -1) - (toNum(b[campo]) ?? -1);

  const colsUnidades = [
    { title: 'U1', key: 'u1', width: 80, align: 'center', sorter: sortNum('PromedioU1'),
      render: (_, r) => <CeldaNota valor={r.PromedioU1} /> },
    { title: 'U2', key: 'u2', width: 80, align: 'center', sorter: sortNum('PromedioU2'),
      render: (_, r) => <CeldaNota valor={r.PromedioU2} /> },
    { title: 'U3', key: 'u3', width: 80, align: 'center', sorter: sortNum('PromedioU3'),
      render: (_, r) => <CeldaNota valor={r.PromedioU3} /> },
    { title: 'U4', key: 'u4', width: 80, align: 'center', sorter: sortNum('PromedioU4'),
      render: (_, r) => <CeldaNota valor={r.PromedioU4} /> },
    {
      title: 'Prom. General', key: 'pg', width: 110, align: 'center', fixed: 'right',
      sorter: sortNum('PromedioGeneral'),
      render: (_, r) => {
        const n = toNum(r.PromedioGeneral);
        if (n === null) return <Tag color="default">-</Tag>;
        const color = n >= 60 ? 'blue' : 'red';
        return <Tag color={color} style={{ fontWeight: 'bold' }}>{n.toFixed(2)}</Tag>;
      },
    },
  ];

  const colAlumno = {
    title: 'Alumno', key: 'alumno', width: 200,
    sorter: (a, b) => `${a.Apellidos} ${a.Nombres}`.localeCompare(`${b.Apellidos} ${b.Nombres}`),
    render: (_, r) => `${r.Apellidos} ${r.Nombres}`,
  };

  // ── Columnas Tab 1 ────────────────────────────────────────────────────────
  const columnasGrado = [
    { title: '#', key: 'idx', width: 50, align: 'center',
      render: (_, __, i) => i + 1 },
    colAlumno,
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 100 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 140,
      sorter: (a, b) => a.NombreGrado.localeCompare(b.NombreGrado) },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 100 },
    ...colsUnidades,
  ];

  // ── Columnas Tab 2 ────────────────────────────────────────────────────────
  const columnasJornada = [
    { title: 'Pos.', key: 'idx', width: 60, align: 'center',
      render: (_, __, i) => i + 1 },
    colAlumno,
    { title: 'Matrícula', dataIndex: 'Matricula', key: 'mat', width: 100 },
    { title: 'Grado', dataIndex: 'NombreGrado', key: 'grado', width: 140,
      sorter: (a, b) => a.NombreGrado.localeCompare(b.NombreGrado) },
    { title: 'Sección', dataIndex: 'NombreSeccion', key: 'sec', width: 80, align: 'center' },
    { title: 'Jornada', dataIndex: 'NombreJornada', key: 'jornada', width: 100 },
    ...colsUnidades,
  ];

  // ── Fila resumen (promedio de promedios) ──────────────────────────────────
  const calcResumen = (data) => {
    const campos = ['PromedioU1', 'PromedioU2', 'PromedioU3', 'PromedioU4', 'PromedioGeneral'];
    return campos.map((c) => {
      const vals = data.map(r => toNum(r[c])).filter(v => v !== null);
      if (vals.length === 0) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
    });
  };

  const Summary = ({ data }) => {
    const [u1, u2, u3, u4, pg] = calcResumen(data);
    if (data.length === 0) return null;
    const cell = (v) => {
      if (v === null) return <Tag color="default">-</Tag>;
      return <Tag color={v >= 60 ? 'blue' : 'red'} style={{ fontWeight: 'bold' }}>{v.toFixed(2)}</Tag>;
    };
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: '#f0f5ff' }}>
          <Table.Summary.Cell index={0} colSpan={6}>
            <strong>Promedio del grupo</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="center">{cell(u1)}</Table.Summary.Cell>
          <Table.Summary.Cell index={7} align="center">{cell(u2)}</Table.Summary.Cell>
          <Table.Summary.Cell index={8} align="center">{cell(u3)}</Table.Summary.Cell>
          <Table.Summary.Cell index={9} align="center">{cell(u4)}</Table.Summary.Cell>
          <Table.Summary.Cell index={10} align="center">{cell(pg)}</Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // ── Exportar Excel ────────────────────────────────────────────────────────
  const exportarExcel = (data, nombreHoja) => {
    if (data.length === 0) { message.warning('No hay datos para exportar'); return; }
    const filas = data.map((r, i) => ({
      '#': i + 1,
      Alumno: `${r.Apellidos} ${r.Nombres}`,
      Matrícula: r.Matricula,
      Grado: r.NombreGrado,
      Sección: r.NombreSeccion,
      Jornada: r.NombreJornada,
      'Prom. U1': toNum(r.PromedioU1),
      'Prom. U2': toNum(r.PromedioU2),
      'Prom. U3': toNum(r.PromedioU3),
      'Prom. U4': toNum(r.PromedioU4),
      'Prom. General': toNum(r.PromedioGeneral),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [
      { wch: 5 }, { wch: 30 }, { wch: 12 }, { wch: 20 },
      { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
      { wch: 10 }, { wch: 10 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, nombreHoja);
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]), `Promedios_${nombreHoja}_${CICLO_ACTUAL}.xlsx`);
  };

  // ── Filtros reutilizables ─────────────────────────────────────────────────
  const SelectCiclo = ({ value, onChange }) => (
    <Select style={{ width: 100 }} value={value} onChange={onChange}>
      {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
    </Select>
  );

  const SelectJornada = ({ value, onChange }) => (
    <Select style={{ width: 130 }} placeholder="Jornada" allowClear value={value} onChange={onChange}
      loading={loadingCatalogos}>
      {jornadas.map(j => <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>)}
    </Select>
  );

  const SelectGrado = ({ value, onChange }) => (
    <Select style={{ width: 180 }} placeholder="Grado" allowClear value={value} onChange={onChange}
      loading={loadingCatalogos}>
      {grados.map(g => <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>)}
    </Select>
  );

  const SelectSeccion = ({ value, onChange }) => (
    <Select style={{ width: 110 }} placeholder="Sección" allowClear value={value} onChange={onChange}
      loading={loadingCatalogos}>
      {secciones.map(s => <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>)}
    </Select>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      <Card title={<span><BarChartOutlined /> Promedios de Calificaciones</span>}>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            // ── Tab 1: Por Grado ──────────────────────────────────────────
            {
              key: 'grado',
              label: 'Por Grado / Sección',
              children: (
                <>
                  <Space wrap style={{ marginBottom: 16 }}>
                    <SelectCiclo
                      value={filtrosGrado.p_CicloEscolar}
                      onChange={v => setFiltrosGrado(f => ({ ...f, p_CicloEscolar: v }))}
                    />
                    <SelectJornada
                      value={filtrosGrado.IdJornada}
                      onChange={v => setFiltrosGrado(f => ({ ...f, IdJornada: v }))}
                    />
                    <SelectGrado
                      value={filtrosGrado.IdGrado}
                      onChange={v => setFiltrosGrado(f => ({ ...f, IdGrado: v, IdSeccion: null }))}
                    />
                    <SelectSeccion
                      value={filtrosGrado.IdSeccion}
                      onChange={v => setFiltrosGrado(f => ({ ...f, IdSeccion: v }))}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={buscarPorGrado}
                      loading={loadingGrado}>
                      Buscar
                    </Button>
                    <Tooltip title="Exportar Excel">
                      <Button icon={<DownloadOutlined />}
                        onClick={() => exportarExcel(dataGrado, 'PorGrado')}
                        disabled={dataGrado.length === 0}>
                        Excel
                      </Button>
                    </Tooltip>
                  </Space>

                  <Table
                    columns={columnasGrado}
                    dataSource={dataGrado}
                    rowKey={(_, i) => `g-${i}`}
                    loading={loadingGrado}
                    pagination={{ pageSize: 20, showSizeChanger: true,
                      showTotal: t => `Total: ${t} alumnos` }}
                    bordered
                    scroll={{ x: 900 }}
                    summary={() => <Summary data={dataGrado} />}
                    locale={{ emptyText: 'Selecciona filtros y presiona Buscar' }}
                  />
                </>
              ),
            },

            // ── Tab 2: Por Jornada (ranking) ──────────────────────────────
            {
              key: 'jornada',
              label: 'Ranking por Jornada',
              children: (
                <>
                  <Space wrap style={{ marginBottom: 16 }}>
                    <SelectCiclo
                      value={filtrosJornada.p_CicloEscolar}
                      onChange={v => setFiltrosJornada(f => ({ ...f, p_CicloEscolar: v }))}
                    />
                    <SelectJornada
                      value={filtrosJornada.IdJornada}
                      onChange={v => setFiltrosJornada(f => ({ ...f, IdJornada: v }))}
                    />
                    <SelectGrado
                      value={filtrosJornada.IdGrado}
                      onChange={v => setFiltrosJornada(f => ({ ...f, IdGrado: v }))}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={buscarPorJornada}
                      loading={loadingJornada}>
                      Buscar
                    </Button>
                    <Tooltip title="Exportar Excel">
                      <Button icon={<DownloadOutlined />}
                        onClick={() => exportarExcel(dataJornada, 'PorJornada')}
                        disabled={dataJornada.length === 0}>
                        Excel
                      </Button>
                    </Tooltip>
                  </Space>

                  <Table
                    columns={columnasJornada}
                    dataSource={dataJornada}
                    rowKey={(_, i) => `j-${i}`}
                    loading={loadingJornada}
                    pagination={{ pageSize: 20, showSizeChanger: true,
                      showTotal: t => `Total: ${t} alumnos` }}
                    bordered
                    scroll={{ x: 900 }}
                    summary={() => <Summary data={dataJornada} />}
                    locale={{ emptyText: 'Selecciona jornada y presiona Buscar' }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default PromediosGrado;
